import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableStorySettings } from 'admin/reducers/card-editor/selectors';
import { selectEditableStory, selectStorycardsDomain } from 'admin/reducers/story-editor/selectors';
import { selectEditableElementId } from 'admin/reducers/card-editor-extra/selectors';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import { getOrganizationQueryOptions } from 'admin/queries/organization/get-organization';
import { getBrandKitById } from 'common/brand-kit/projection';
import {
	assetIdToReference,
	copyDesignDefinition,
	createDesignDefinition,
	getDesignAssetReferenceId,
	importDesignAssets,
	resolveDesignAssetReference,
	resolveDesignModeValue,
	type DesignAsset,
	type DesignModeSelection,
	type DesignModeValue,
} from 'common/design-tokens';
import { cutURLMethod, replaceUrlHostname } from 'common/utils/assets';
import { PICTURES_FILE_TYPES } from 'utils/assets';
import Button from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import { toast } from 'admin/components/common/MessageContainer/toast';
import DesignModeValueField from 'admin/components/common/DesignModeValueField';
import DashboardTooltip from 'admin/components/common/Tooltip';
import SearchField from 'admin/components/common/SearchField';
import PanelTooltip from 'admin/components/pages/Story/PanelTooltip';
import { useCenterSelected } from 'common/hooks/use-center-selected';
import t from 'utils/translate';
import type { DesignBindingControlProps } from './DesignTokenControl';
import { useInspectorDesignModeSelection } from './useInspectorDesignModeSelection';
import css from './DesignTokenControl.scss';
import TokenPill from './TokenPill';
import TokenEditorPanel from './TokenEditorPanel';
import { MediaField } from './MediaField';
import { useStandaloneField } from './use-standalone-field';
import { useMediaUpload } from './use-media-upload';
import { usePopoverChildProps } from '../Popover';

type Source = 'story' | 'brandKit';

/** The picker's scrolling list; it opens on the linked asset */
function AssetList({ children }: { children: React.ReactNode }) {
	const listRef = useRef<HTMLDivElement>(null);
	useCenterSelected(listRef);
	return (
		<div ref={listRef} className={css.tokenList}>
			{children}
		</div>
	);
}

/** An asset's image for one mode, set as an Image field sets its image: preview with clear, or upload and From Link */
function AssetValueField({
	name,
	value,
	onChange,
	onBusyChange,
}: {
	name: string;
	value: string;
	onChange: (value: string) => void;
	onBusyChange: (busy: boolean) => void;
}) {
	const upload = useMediaUpload();
	const { input, meta } = useStandaloneField({
		form: 'design-asset',
		name,
		value,
		onChange: next => onChange(String(next ?? '')),
	});
	return (
		<MediaField
			input={input}
			meta={meta}
			showLink
			customData={{ isRemovable: true, fileTypes: PICTURES_FILE_TYPES }}
			eventListeners={{ onUpload: upload }}
			onBusyChange={onBusyChange}
		/>
	);
}

export default function DesignAssetControl({ literalComponent: Literal, ...props }: DesignBindingControlProps) {
	const settings = useAdminSelector(selectEditableStorySettings);
	const story = useAdminSelector(selectEditableStory);
	const elementId = useAdminSelector(selectEditableElementId);
	const activeOrganization = useAdminSelector(selectActiveOrganization);
	const domain = useAdminSelector(selectStorycardsDomain);
	const selection = useInspectorDesignModeSelection();
	const organizationQuery = useQuery({
		...getOrganizationQueryOptions(story?.organizationId ?? ''),
		enabled: !!story?.organizationId && story.organizationId === activeOrganization?.id,
	});
	const organization =
		organizationQuery.data ?? (activeOrganization?.id === story?.organizationId ? activeOrganization : undefined);
	const library = getBrandKitById(organization?.metadata?.brandKits, settings.brandKitId)?.design;
	const design = settings?.design ?? createDesignDefinition();
	const raw = String(props.input.value || props.forcedValue || '');
	const assetId = getDesignAssetReferenceId(raw);
	const asset = design.assets.assets.find(item => item.id === assetId);
	const resolved = resolveDesignAssetReference(raw, design, selection) ?? '';
	const [open, setOpen] = useState(false);
	const popoverChildProps = usePopoverChildProps();
	const [search, setSearch] = useState('');
	const [draft, setDraft] = useState<DesignModeValue<string>>();
	const [busy, setBusy] = useState(false);
	const [uploading, setUploading] = useState(false);
	const latest = useRef({ settings, fieldKey: '', raw });
	const fieldKey = `${story?.id}:${elementId}:${props.input.name}`;
	latest.current = { settings, fieldKey, raw };
	const editContext = useRef<{
		settings: typeof settings;
		selection: DesignModeSelection;
		assetId: string;
		raw: string;
		fieldKey: string;
	}>();
	useEffect(
		() => () => {
			latest.current.fieldKey = '';
		},
		[]
	);
	const close = () => {
		setOpen(false);
		setSearch('');
		setDraft(undefined);
		editContext.current = undefined;
	};
	useEffect(close, [fieldKey]);

	const backToPicker = () => {
		setDraft(undefined);
		editContext.current = undefined;
	};
	const openPicker = () => {
		backToPicker();
		setOpen(true);
	};
	const openEditor = (item: DesignAsset) => {
		editContext.current = { settings, selection, assetId: item.id, raw, fieldKey };
		setDraft(item.value);
		setOpen(true);
	};
	const apply = async (value: string, source: Source = 'story') => {
		setBusy(true);
		try {
			const id = getDesignAssetReferenceId(value);
			const nextDesign =
				source === 'brandKit' && library && id ? importDesignAssets(design, library, [id]) : design;
			if (latest.current.fieldKey !== fieldKey || latest.current.settings !== settings) return;
			if (!props.eventListeners?.onTokenChange) throw new Error('Asset binding is unavailable for this field.');
			await props.eventListeners.onTokenChange({
				name: props.input.name,
				value,
				settings: { ...settings, ...(nextDesign !== design ? { design: nextDesign } : {}) },
			});
			if (latest.current.fieldKey !== fieldKey) return;
			props.input.onChange(value);
			close();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};
	const save = async () => {
		const context = editContext.current;
		if (!context || !draft) return;
		setBusy(true);
		try {
			if (
				latest.current.fieldKey !== context.fieldKey ||
				latest.current.settings !== context.settings ||
				latest.current.raw !== context.raw
			)
				throw new Error('Story or selection changed. Reopen the asset editor.');
			const nextDesign = copyDesignDefinition(design);
			const nextAsset = nextDesign.assets.assets.find(item => item.id === context.assetId);
			if (!nextAsset) throw new Error('Asset is unavailable.');
			nextAsset.value = draft;
			if (!props.eventListeners?.onTokenValueChange)
				throw new Error('Asset editing is unavailable for this field.');
			await props.eventListeners.onTokenValueChange({
				...settings,
				design: copyDesignDefinition(nextDesign),
			});
			backToPicker();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};
	const query = search.trim().toLowerCase();
	const filter = (item: DesignAsset) =>
		!query || [item.name, item.role, item.id].some(value => value.toLowerCase().includes(query));
	const storyAssets = design.assets.assets.filter(filter);
	const storyAssetIds = new Set(design.assets.assets.map(item => item.id));
	const matchingLibraryAssets = (library?.assets.assets ?? []).filter(filter);
	// Brand Kit assets the Story already has are picked from the Story section; the heading counts them
	const libraryAssets = matchingLibraryAssets.filter(item => !storyAssetIds.has(item.id));
	const libraryAssetsInStory = matchingLibraryAssets.length - libraryAssets.length;
	const editedAsset = design.assets.assets.find(item => item.id === editContext.current?.assetId);
	// An asset always has an image: after clearing it, Save waits for a new one
	const editedValue =
		draft && editContext.current ? resolveDesignModeValue(draft, editContext.current.selection) : '';
	const editingModes = design.modeGroups
		.map(group => group.modes.find(mode => mode.id === editContext.current?.selection[group.id])?.name)
		.filter(Boolean)
		.join(' / ');
	// Unlink keeps the image the field shows now as a plain value; a missing asset has nothing to keep
	const unlinkLabel = asset ? t('designTokens.unlink') : 'Clear';
	const unlinkHint = asset
		? "Keep this image without the asset. Later changes to the asset don't reach it."
		: 'Remove the reference to the missing asset.';
	const content =
		draft && editContext.current ? (
			<TokenEditorPanel
				className={css.assetPanel}
				title="Edit asset"
				subject={`${editedAsset?.name ?? 'Asset unavailable'} · ${editingModes}`}
				help={
					editedValue
						? 'Updates every use of this asset in this Story.'
						: 'Upload an image or paste a link to save.'
				}
				busy={busy}
				canSave={!!editedAsset && !!editedValue && !uploading}
				onBack={backToPicker}
				onSave={save}
			>
				<DesignModeValueField
					value={draft}
					groups={design.modeGroups}
					selection={editContext.current.selection}
					disabled={busy}
					onChange={setDraft}
				>
					{({ value, setValue }) => (
						<AssetValueField
							name={editedAsset?.id ?? 'asset'}
							value={value}
							onChange={setValue}
							onBusyChange={setUploading}
						/>
					)}
				</DesignModeValueField>
			</TokenEditorPanel>
		) : (
			<div className={css.assetPanel} onKeyDown={event => event.key === 'Escape' && close()}>
				<header className={css.pickerHeader}>
					<strong>Connect asset</strong>
				</header>
				<SearchField
					variant="embedded"
					size="sm"
					divider
					autoFocus
					placeholder="Search assets"
					value={search}
					onChange={setSearch}
				/>
				<AssetList>
					{(
						[
							{ source: 'story', name: 'Story', assets: storyAssets, hiddenCount: 0 },
							{
								source: 'brandKit',
								name: 'Brand Kit',
								assets: libraryAssets,
								hiddenCount: libraryAssetsInStory,
							},
						] as const
					).map(section =>
						section.assets.length ? (
							<section className={css.source} key={section.source}>
								<h3>
									{section.name}
									{section.hiddenCount > 0 ? (
										<span>{section.hiddenCount} already in Story</span>
									) : null}
								</h3>
								{section.assets.map(item => {
									const selected = section.source === 'story' && item.id === assetId;
									return (
										<div className={css.assetItem} key={item.id}>
											<button
												type="button"
												className={css.assetRow}
												data-selected={selected}
												aria-pressed={selected}
												disabled={props.disabled || busy}
												onClick={() => apply(assetIdToReference(item.id), section.source)}
											>
												<img
													className={css.assetThumbnail}
													src={replaceUrlHostname(
														cutURLMethod(resolveDesignModeValue(item.value, selection)),
														domain
													)}
													alt=""
												/>
												<span className={css.rowName}>{item.name}</span>
												<span className={css.rowValue}>{item.role}</span>
												{selected ? (
													<CheckOutlined className={css.assetCheck} aria-hidden />
												) : null}
											</button>
											{section.source === 'story' ? (
												<PanelTooltip content="Edit asset value" placement="left">
													<button
														type="button"
														className={css.rowEdit}
														data-selected={selected}
														aria-label={`Edit asset value: ${item.name}`}
														disabled={props.disabled || busy}
														onClick={() => openEditor(item)}
													>
														<EditOutlined />
													</button>
												</PanelTooltip>
											) : null}
										</div>
									);
								})}
							</section>
						) : null
					)}
					{!storyAssets.length && !libraryAssets.length ? (
						<div className={css.empty}>No matching assets.</div>
					) : null}
				</AssetList>
				{assetId ? (
					<footer className={css.pickerFooter}>
						<PanelTooltip content={unlinkHint} placement="top">
							<Button
								view="label-only"
								size="tiny"
								theme="dark"
								color="white"
								textSize={Text.size.description}
								textWeight={Text.weight.normal}
								disabled={props.disabled || busy}
								onClick={() => apply(asset ? resolved : '')}
							>
								{unlinkLabel}
							</Button>
						</PanelTooltip>
						<span className={css.pickerHint}>{t('designTokens.escToClose')}</span>
					</footer>
				) : null}
			</div>
		);

	return (
		<div className={css.control}>
			<Literal {...props} previewValue={assetId ? resolved : undefined} onReferenceChange={apply} />
			<div className={css.assetBinding}>
				<DashboardTooltip
					stylePreset="dashboard"
					destroyOnHidden
					trigger="click"
					open={open}
					onOpenChange={next => (next ? setOpen(true) : close())}
					content={<div {...popoverChildProps}>{content}</div>}
					placement="left"
					autoAdjustOverflow
				>
					<span className={css.binding}>
						{assetId ? (
							<TokenPill
								className={css.pill}
								label={`Change asset: ${asset?.name ?? raw}`}
								value={asset ? asset.name : 'Unresolved asset'}
								tooltip={<span className={css.tooltipLabel}>{asset ? asset.name : raw}</span>}
								unresolved={!asset}
								open={open}
								disabled={props.disabled || busy}
								onClick={openPicker}
								onUnlink={() => apply(asset ? resolved : '')}
								unlinkLabel={unlinkLabel}
							/>
						) : (
							<PanelTooltip content="Connect asset" open={open ? false : undefined}>
								<button
									type="button"
									className={css.tokenButton}
									data-open={open}
									aria-label="Connect asset"
									aria-expanded={open}
									disabled={props.disabled || busy}
									onClick={openPicker}
								/>
							</PanelTooltip>
						)}
					</span>
				</DashboardTooltip>
			</div>
		</div>
	);
}
